import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  UsersThree,
  Plus,
  Trash,
  Check,
  Clock,
  PaperPlaneTilt,
  Sparkle,
  ShieldCheck,
  X,
  PencilSimple,
  ChatTeardropDots,
  ChatCircleText as ChatCircleTextIcon,
} from "@phosphor-icons/react";
import * as API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../components/ui/tabs";
import ItemMenu from "../components/ItemMenu";
import { toast } from "sonner";
import { useDraft } from "../hooks/useDraft";

const STATUS_LABEL = {
  // legacy + V9.1 unified
  pending: "Pending",
  nudged: "Nudged",
  working_on_it: "Working on it",
  stuck: "Stuck",
  ignored: "Ignored",
  completed: "Completed",
  draft: "Draft",
  approved: "Approved",
  sent: "Sent",
  in_progress: "In progress",
  done: "Done",
  cancelled: "Cancelled",
};
const STATUS_COLOR = {
  pending: "bg-white/5 text-white/60 border-white/10",
  nudged: "bg-cyan-500/10 text-cyan-300 border-cyan-500/25",
  working_on_it: "bg-amber-500/10 text-amber-300 border-amber-500/25",
  stuck: "bg-orange-500/15 text-orange-300 border-orange-500/30",
  ignored: "bg-white/5 text-white/40 border-white/10",
  completed: "bg-emerald-500/10 text-emerald-300 border-emerald-500/25",
  // legacy
  draft: "bg-white/5 text-white/60 border-white/10",
  sent: "bg-cyan-500/10 text-cyan-300 border-cyan-500/25",
  in_progress: "bg-amber-500/10 text-amber-300 border-amber-500/25",
  done: "bg-emerald-500/10 text-emerald-300 border-emerald-500/25",
  cancelled: "bg-white/5 text-white/40 border-white/10",
};

// V9.29 — For delegated tasks the owner only sees high-level labels
// (the 9 owner-allowed display statuses set by the backend).
// `display_status` arrives from /api/tasks already lowercase.
const DISPLAY_STATUS_COLOR = {
  "done": "bg-emerald-500/10 text-emerald-300 border-emerald-500/25",
  "not done": "bg-white/5 text-white/60 border-white/10",
  "ignored": "bg-white/5 text-white/40 border-white/10",
  "postponed": "bg-purple-500/10 text-purple-300 border-purple-500/25",
  "needs help": "bg-orange-500/15 text-orange-300 border-orange-500/30",
  "in progress": "bg-amber-500/10 text-amber-300 border-amber-500/25",
  "awaiting reply": "bg-cyan-500/10 text-cyan-300 border-cyan-500/25",
  "overdue": "bg-red-500/15 text-red-300 border-red-500/30",
  "unable to complete": "bg-white/5 text-white/40 border-white/10",
  // V9.39 — scheduled-but-not-yet-sent delegations.
  "scheduled": "bg-violet-500/10 text-violet-300 border-violet-500/30",
};
function statusBadgeFor(task) {
  // Delegated tasks → high-level label only (owner privacy).
  // Non-delegated → existing detailed internal status.
  if (task && task.delegated && task.display_status) {
    return {
      label: task.display_status.toUpperCase(),
      cls:
        DISPLAY_STATUS_COLOR[task.display_status] ||
        DISPLAY_STATUS_COLOR["not done"],
    };
  }
  return {
    label: STATUS_LABEL[task.status] || task.status,
    cls: STATUS_COLOR[task.status] || STATUS_COLOR.draft,
  };
}

const ACTIVE_STATUSES = new Set(["pending", "scheduled", "nudged", "working_on_it", "stuck", "draft", "approved", "sent", "in_progress"]);
const TERMINAL_STATUSES = new Set(["completed", "ignored", "done", "cancelled"]);

export default function Tasks() {
  const [tab, setTab] = useState("active"); // active | done
  const [tasks, setTasks] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [transport, setTransport] = useState({ enabled: false, provider: "stub" });

  const [showCreate, setShowCreate] = useState(false);
  const [openTaskId, setOpenTaskId] = useState(null);
  const [showContacts, setShowContacts] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [t, c, ts] = await Promise.all([
        API.listTasks(),
        API.listContacts(),
        API.taskTransportStatus(),
      ]);
      setTasks(t);
      setContacts(c);
      setTransport(ts);
    } catch {
      toast.error("Failed to load tasks");
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // V9.27 — When the chat tool `delegate_to_contact` fires, Chat.js
  // dispatches a `journey:task-delegated` window event. Listen for it
  // here so the Tasks page reflects the new delegation immediately
  // without the user having to manually navigate or refresh.
  useEffect(() => {
    const onDelegated = () => refresh();
    window.addEventListener("journey:task-delegated", onDelegated);
    return () =>
      window.removeEventListener("journey:task-delegated", onDelegated);
  }, [refresh]);

  const active = tasks.filter((t) => ACTIVE_STATUSES.has(t.status));
  const archived = tasks.filter((t) => TERMINAL_STATUSES.has(t.status));

  const onCreated = (task) => {
    setShowCreate(false);
    refresh();
    setOpenTaskId(task.id);
  };

  return (
    <div className="overflow-y-auto scroll-contain" data-testid="tasks-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <div className="flex items-start justify-between gap-3 mb-7">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center shrink-0">
              <UsersThree size={22} weight="duotone" className="text-cyan-400" />
            </div>
            <div>
              <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
                Tasks
              </h1>
              <p className="text-white/60 text-sm mt-1">
                Delegate, draft, approve, and follow up — Journey assists, you decide.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="outline"
              onClick={() => setShowContacts(true)}
              className="border-white/10 hover:bg-white/5"
              data-testid="tasks-contacts-button"
            >
              <UsersThree size={16} className="mr-1.5" />
              Contacts
            </Button>
            <Button
              onClick={() => setShowCreate(true)}
              className="bg-cyan-500 hover:bg-cyan-400 text-black font-medium rounded-xl"
              data-testid="task-new-button"
            >
              <Plus size={16} weight="bold" className="mr-1" />
              New
            </Button>
          </div>
        </div>

        {!transport.enabled && (
          <div
            className="mb-5 px-4 py-2.5 rounded-xl bg-amber-500/5 border border-amber-500/20 text-[12px] text-amber-300 flex items-center gap-2"
            data-testid="transport-stub-banner"
          >
            <ShieldCheck size={14} weight="duotone" />
            SMS transport is in <strong className="font-semibold">stub mode</strong> —
            messages are logged but not delivered. Add Twilio credentials to enable real SMS.
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-[#0f0f0f] border border-white/5 mb-4">
            <TabsTrigger value="active" data-testid="tab-active">
              Active ({active.length})
            </TabsTrigger>
            <TabsTrigger value="done" data-testid="tab-done">
              Archived ({archived.length})
            </TabsTrigger>
          </TabsList>
          <TabsContent value="active">
            <TaskList
              tasks={active}
              contacts={contacts}
              onOpen={setOpenTaskId}
              onChange={refresh}
              empty="No active tasks. Create one to delegate work."
            />
          </TabsContent>
          <TabsContent value="done">
            <TaskList
              tasks={archived}
              contacts={contacts}
              onOpen={setOpenTaskId}
              onChange={refresh}
              empty="No archived tasks yet."
            />
          </TabsContent>
        </Tabs>
      </div>

      <AnimatePresence>
        {showCreate && (
          <CreateTaskDialog
            contacts={contacts}
            onClose={() => setShowCreate(false)}
            onCreated={onCreated}
          />
        )}
        {showContacts && (
          <ContactsDialog
            contacts={contacts}
            onClose={() => setShowContacts(false)}
            onChange={refresh}
          />
        )}
        {openTaskId && (
          <TaskDetail
            taskId={openTaskId}
            contacts={contacts}
            onClose={() => setOpenTaskId(null)}
            onChange={refresh}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function TaskList({ tasks, contacts, onOpen, onChange, empty }) {
  if (tasks.length === 0)
    return <p className="text-sm text-white/40 px-1 py-4">{empty}</p>;
  const contactMap = Object.fromEntries(contacts.map((c) => [c.id, c]));
  return (
    <ul className="space-y-2.5">
      {tasks.map((t) => {
        const c = t.contact_id ? contactMap[t.contact_id] : null;
        return (
          <li key={t.id}>
            <div
              role="button"
              tabIndex={0}
              onClick={() => onOpen(t.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onOpen(t.id);
                }
              }}
              data-testid={`task-card-${t.id}`}
              className="w-full text-left rounded-xl bg-[#0f0f0f] border border-white/5 hover:border-cyan-500/30 px-4 py-3.5 flex items-start gap-3 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/40"
            >
              <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/5 flex items-center justify-center shrink-0">
                <ChatTeardropDots size={16} weight="duotone" className="text-cyan-400/80" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                  <span className="text-[15px] text-white/90 font-medium truncate">
                    {t.title}
                  </span>
                  {(() => {
                    const b = statusBadgeFor(t);
                    return (
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider ${b.cls}`}
                        data-testid={`task-status-${t.id}`}
                      >
                        {b.label}
                      </span>
                    );
                  })()}
                  {t.delegated ? (
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded-full border border-cyan-500/30 bg-cyan-500/10 text-cyan-300 text-[10px] uppercase tracking-wider"
                      data-testid={`task-delegated-badge-${t.id}`}
                    >
                      Delegated
                    </span>
                  ) : null}
                  {t.deadline_at ? (
                    <DeadlineChip
                      iso={t.deadline_at}
                      testId={`task-deadline-${t.id}`}
                    />
                  ) : null}
                  {/* V9.44.2 — Recipient badge. Tasks are inherently */}
                  {/* contact-directed (the contact gets the SMS; the */}
                  {/* owner gets terminal-status updates only). Always */}
                  {/* show who the outreach is going to so it's */}
                  {/* obvious this is delegated work, not a personal */}
                  {/* reminder. */}
                  {c ? (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-sky-400/25 bg-sky-500/12 text-sky-200 text-[10px] uppercase tracking-wider"
                      data-testid={`task-recipient-${t.id}`}
                      title={`SMS goes to ${c.name}`}
                    >
                      <ChatCircleTextIcon size={10} weight="fill" />
                      To {c.name}
                    </span>
                  ) : null}
                </div>
                <div className="text-xs text-white/50 truncate">
                  {c ? `→ ${c.name}` : "Unassigned"}
                  {t.next_followup_at &&
                    ` · follow-up ${new Date(t.next_followup_at).toLocaleString()}`}
                </div>
                {/* V9.39 — Scheduled-send subline. Show the upcoming
                    fire time so the owner can verify they won't text
                    the contact at a surprising moment. For recurring
                    tasks this advances after every successful send. */}
                {t.next_fire_at && (t.status === "scheduled" || t.delegated) ? (
                  <div
                    className="text-[11px] text-violet-300/80 truncate mt-0.5"
                    data-testid={`task-next-fire-${t.id}`}
                  >
                    {(t.repeat_type === "daily" || t.repeat_type === "weekly")
                      ? `Next text · ${new Date(t.next_fire_at).toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" })} · ${t.repeat_type === "weekly" ? "weekly" : "daily"}`
                      : `Scheduled · ${new Date(t.next_fire_at).toLocaleString()}`}
                  </div>
                ) : null}
                {/* V9.29 — Final-answer inline preview removed for
                    delegated tasks. Owner only sees high-level status
                    (the badge above); the contact's verbatim words
                    stay private between them and Journey. */}
                {!t.delegated && t.final_answer ? (
                  <div
                    className="mt-1.5 text-[12.5px] text-emerald-200/80 line-clamp-2"
                    data-testid={`task-final-answer-${t.id}`}
                  >
                    <span className="text-emerald-400/70 mr-1">↳</span>
                    {t.final_answer}
                  </div>
                ) : null}
              </div>
              <div
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <ItemMenu
                  testId={`task-menu-${t.id}`}
                  items={[
                    {
                      label: "Mark done",
                      icon: Check,
                      onClick: async () => {
                        await API.completeTask(t.id);
                        onChange();
                      },
                      testId: `task-done-${t.id}`,
                    },
                    {
                      label: "Follow-up in 24h",
                      icon: Clock,
                      onClick: async () => {
                        await API.followupTask(t.id, 24);
                        onChange();
                      },
                      testId: `task-followup-${t.id}`,
                    },
                    { type: "separator" },
                    {
                      label: "Delete",
                      icon: Trash,
                      onClick: async () => {
                        await API.deleteTask(t.id);
                        onChange();
                      },
                      destructive: true,
                      testId: `task-delete-${t.id}`,
                    },
                  ]}
                />
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function ModalShell({ children, onClose, testId, wide }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-6"
      data-testid={testId}
    >
      <motion.div
        initial={{ y: 24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 24, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className={`bg-[#0f0f0f] border border-white/10 w-full ${
          wide ? "sm:max-w-2xl" : "sm:max-w-md"
        } rounded-t-3xl sm:rounded-3xl max-h-[92vh] overflow-y-auto`}
      >
        {children}
      </motion.div>
    </motion.div>
  );
}

function CreateTaskDialog({ contacts, onClose, onCreated }) {
  const [draft, setDraft, clearDraft] = useDraft("task-new", {
    title: "",
    desc: "",
    contactId: "",
  });
  const title = draft.title;
  const desc = draft.desc;
  const contactId = draft.contactId;
  const setTitle = (v) => setDraft((d) => ({ ...d, title: v }));
  const setDesc = (v) => setDraft((d) => ({ ...d, desc: v }));
  const setContactId = (v) => setDraft((d) => ({ ...d, contactId: v }));
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!title.trim()) {
      toast.error("Title required");
      return;
    }
    setSaving(true);
    try {
      const t = await API.createTask({
        title: title.trim(),
        description: desc.trim(),
        contact_id: contactId || null,
        needs_approval: true,
      });
      setDraft({ title: "", desc: "", contactId: "" });
      clearDraft();
      onCreated(t);
    } catch {
      toast.error("Failed to create");
    } finally {
      setSaving(false);
    }
  };
  return (
    <ModalShell onClose={onClose} testId="create-task-dialog">
      <div className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-medium">New task</h2>
          <button onClick={onClose} className="p-2 -m-1 text-white/50 hover:text-white" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <Input
          autoFocus
          placeholder="What needs to happen?"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="bg-transparent border-white/10"
          data-testid="task-title-input"
        />
        <Textarea
          placeholder="Optional details / context"
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          className="bg-transparent border-white/10 min-h-[70px]"
          data-testid="task-desc-input"
        />
        <div>
          <label className="text-xs text-white/55 mb-1 block">Assign to (optional)</label>
          <Select value={contactId} onValueChange={setContactId}>
            <SelectTrigger
              className="bg-transparent border-white/10 text-left"
              data-testid="task-contact-select"
            >
              <SelectValue placeholder="No contact" />
            </SelectTrigger>
            <SelectContent className="bg-[#161616] border-white/10 text-white">
              {contacts.length === 0 && (
                <SelectItem disabled value="__none__">
                  No contacts yet
                </SelectItem>
              )}
              {contacts.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name} {c.relationship && `· ${c.relationship}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} className="text-white/70">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-cyan-500 hover:bg-cyan-400 text-black font-medium rounded-xl"
            data-testid="task-create-submit"
          >
            Create task
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

function ContactsDialog({ contacts, onClose, onChange }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [relationship, setRelationship] = useState("");
  const [saving, setSaving] = useState(false);
  const add = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await API.createContact({
        name: name.trim(),
        phone: phone.trim(),
        relationship: relationship.trim(),
      });
      setName("");
      setPhone("");
      setRelationship("");
      onChange();
    } catch {
      toast.error("Failed to add");
    } finally {
      setSaving(false);
    }
  };
  return (
    <ModalShell onClose={onClose} testId="contacts-dialog">
      <div className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-medium">Contacts</h2>
          <button onClick={onClose} className="p-2 -m-1 text-white/50 hover:text-white" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Input
            placeholder="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="bg-transparent border-white/10 col-span-2"
            data-testid="contact-name-input"
          />
          <Input
            placeholder="Phone (+1…)"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="bg-transparent border-white/10"
            data-testid="contact-phone-input"
          />
          <Input
            placeholder="Relationship"
            value={relationship}
            onChange={(e) => setRelationship(e.target.value)}
            className="bg-transparent border-white/10"
            data-testid="contact-rel-input"
          />
          <Button
            onClick={add}
            disabled={saving || !name.trim()}
            className="bg-cyan-500 hover:bg-cyan-400 text-black col-span-2 font-medium rounded-xl"
            data-testid="contact-add-button"
          >
            <Plus size={14} weight="bold" className="mr-1" />
            Add contact
          </Button>
        </div>
        <ul className="space-y-1 pt-2">
          {contacts.length === 0 && (
            <li className="text-sm text-white/40">No contacts yet.</li>
          )}
          {contacts.map((c) => (
            <li
              key={c.id}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#0f0f0f] border border-white/5"
              data-testid={`contact-row-${c.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white/90 truncate">{c.name}</div>
                <div className="text-[11px] text-white/45">
                  {c.phone || "—"} {c.relationship && `· ${c.relationship}`}
                </div>
              </div>
              <button
                onClick={async () => {
                  await API.deleteContact(c.id);
                  onChange();
                }}
                className="p-3 -m-1.5 text-white/40 hover:text-red-400"
                data-testid={`contact-delete-${c.id}`}
                aria-label="Delete contact"
              >
                <Trash size={14} />
              </button>
            </li>
          ))}
        </ul>
      </div>
    </ModalShell>
  );
}

function TaskDetail({ taskId, contacts, onClose, onChange }) {
  const [task, setTask] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraftState, clearDraft] = useDraft(`task-compose:${taskId}`, {
    intent: "",
    text: "",
  });
  const intent = draft.intent;
  const draftText = draft.text;
  const setIntent = (v) => setDraftState((d) => ({ ...d, intent: v }));
  const setDraftText = (v) => setDraftState((d) => ({ ...d, text: v }));
  const [drafting, setDrafting] = useState(false);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const [tList, msgs] = await Promise.all([
        API.listTasks(),
        API.listTaskMessages(taskId),
      ]);
      setTask(tList.find((t) => t.id === taskId) || null);
      setMessages(msgs);
    } catch {
      toast.error("Failed to load task");
    }
  }, [taskId]);

  useEffect(() => {
    reload();
  }, [reload]);

  if (!task) {
    return (
      <ModalShell onClose={onClose} testId="task-detail-loading" wide>
        <div className="p-8 text-center text-white/40 text-sm">Loading…</div>
      </ModalShell>
    );
  }

  const contact = contacts.find((c) => c.id === task.contact_id);

  const generateDraft = async () => {
    setDrafting(true);
    try {
      const d = await API.draftTaskMessage(task.id, intent);
      setDraftText(d.content);
    } catch {
      toast.error("Draft failed");
    } finally {
      setDrafting(false);
    }
  };

  const queueDraft = async () => {
    if (!draftText.trim()) return;
    setBusy(true);
    try {
      await API.queueTaskMessage(task.id, draftText.trim());
      setDraftState({ intent: "", text: "" });
      clearDraft();
      await reload();
      toast("Queued — review and approve to send", { duration: 4000 });
    } catch {
      toast.error("Queue failed");
    } finally {
      setBusy(false);
    }
  };

  const approveSend = async (mid) => {
    setBusy(true);
    try {
      await API.approveSendTaskMessage(task.id, mid);
      await reload();
      onChange();
      toast.success("Approved & sent");
    } catch {
      toast.error("Send failed");
    } finally {
      setBusy(false);
    }
  };

  const reject = async (mid) => {
    setBusy(true);
    try {
      await API.rejectTaskMessage(task.id, mid);
      await reload();
    } catch {
      toast.error("Failed");
    } finally {
      setBusy(false);
    }
  };

  const startAgent = async () => {
    setBusy(true);
    try {
      const t = await API.startTaskAgent(task.id, true);
      await reload();
      onChange();
      toast.success(`Agent started — first nudge generated (dry-run, status: ${t.status})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Start failed");
    } finally {
      setBusy(false);
    }
  };

  const simulate = async (text) => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const r = await API.simulateInbound(task.id, text.trim(), true);
      await reload();
      onChange();
      toast.success(`Intent: ${r.intent.intent} (${Math.round(r.intent.confidence*100)}%) → ${r.new_status}`);
    } catch {
      toast.error("Simulate failed");
    } finally {
      setBusy(false);
    }
  };

  const overrideStatus = async (status) => {
    setBusy(true);
    try {
      await API.setTaskStatus(task.id, status);
      await reload();
      onChange();
      toast.success(`Marked ${STATUS_LABEL[status] || status}`);
    } catch {
      toast.error("Status update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell onClose={onClose} testId={`task-detail-${task.id}`} wide>
      <div className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            {(() => {
              const b = statusBadgeFor(task);
              return (
                <div
                  className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider mb-1.5 ${b.cls}`}
                  data-testid={`task-detail-status-${task.id}`}
                >
                  {b.label}
                </div>
              );
            })()}
            <h2 className="font-display text-xl font-medium tracking-tight">{task.title}</h2>
            {task.description && (
              <p className="text-sm text-white/60 mt-1">{task.description}</p>
            )}
            {contact && (
              <p className="text-xs text-white/50 mt-2">
                → {contact.name}{" "}
                {contact.phone && <span className="text-white/35">· {contact.phone}</span>}
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-2 -m-1 text-white/50 hover:text-white shrink-0" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* V9.1 — Journey Agent panel */}
        <AgentPanel
          task={task}
          onStart={startAgent}
          onSimulate={simulate}
          onOverride={overrideStatus}
          busy={busy}
        />

        {/* V9.29 — Owner privacy. For DELEGATED tasks the compose
            workflow and the message thread stay HIDDEN: the
            conversation between the contact and Journey is private,
            so the owner UI must not show the verbatim text. The
            agent panel above still shows the high-level state +
            "Mark done / Override status" buttons so the owner can
            still supervise progress. */}
        {!task.delegated && (
          <>
            {/* Compose / Draft area */}
            <div className="rounded-2xl bg-[#0a0a0a] border border-white/5 p-4 space-y-2">
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-medium">
            Compose on behalf
          </div>
          <Input
            placeholder="What's the intent? e.g. 'ask if they can confirm by Friday'"
            value={intent}
            onChange={(e) => setIntent(e.target.value)}
            className="bg-transparent border-white/10"
            data-testid="task-intent-input"
          />
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={generateDraft}
              disabled={drafting}
              className="border-white/10 hover:bg-white/5"
              data-testid="task-draft-button"
            >
              <Sparkle size={14} weight="fill" className="mr-1.5 text-cyan-400" />
              {drafting ? "Drafting…" : "AI draft"}
            </Button>
          </div>
          <Textarea
            placeholder="Draft text — review carefully before queueing"
            value={draftText}
            onChange={(e) => setDraftText(e.target.value)}
            className="bg-transparent border-white/10 min-h-[72px]"
            data-testid="task-draft-textarea"
          />
          <div className="flex justify-end">
            <Button
              onClick={queueDraft}
              disabled={busy || !draftText.trim()}
              className="bg-cyan-500 hover:bg-cyan-400 text-black font-medium rounded-xl"
              data-testid="task-queue-button"
            >
              <ShieldCheck size={14} weight="bold" className="mr-1.5" />
              Queue for approval
            </Button>
          </div>
        </div>

        {/* Communication log */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-medium mb-2">
            History
          </div>
          {messages.length === 0 ? (
            <p className="text-sm text-white/40">No messages yet.</p>
          ) : (
            <ul className="space-y-2">
              {messages.map((m) => (
                <li
                  key={m.id}
                  className="rounded-xl bg-[#0a0a0a] border border-white/5 p-3"
                  data-testid={`taskmsg-row-${m.id}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5 text-[11px]">
                      <span className="text-white/50 uppercase tracking-wider">
                        {m.direction}
                      </span>
                      <StatusDot status={m.status} />
                      <span className="text-white/40">{m.status}</span>
                    </div>
                    <span className="text-[10px] text-white/35">
                      {new Date(m.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm text-white/85 whitespace-pre-wrap break-words">
                    {m.content}
                  </p>
                  {m.status === "pending_approval" && (
                    <div className="flex gap-2 mt-2">
                      <Button
                        size="sm"
                        onClick={() => approveSend(m.id)}
                        disabled={busy}
                        className="bg-cyan-500 hover:bg-cyan-400 text-black h-8 rounded-full"
                        data-testid={`taskmsg-approve-${m.id}`}
                      >
                        <PaperPlaneTilt size={12} weight="bold" className="mr-1" />
                        Approve & send
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => reject(m.id)}
                        disabled={busy}
                        className="text-white/60 hover:text-white h-8 rounded-full"
                        data-testid={`taskmsg-reject-${m.id}`}
                      >
                        Reject
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
          </>
        )}

        {/* V9.29 — For delegated tasks, surface a clear privacy note
            in place of the conversation thread so the owner
            understands why content isn't shown. */}
        {task.delegated && (
          <div
            className="rounded-2xl bg-[#0a0a0a] border border-white/5 p-4 text-[12.5px] text-white/55 leading-relaxed"
            data-testid={`task-delegated-privacy-note-${task.id}`}
          >
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-medium mb-1.5">
              Private conversation
            </div>
            Journey is handling this directly with{" "}
            <span className="text-white/75">
              {contact ? contact.name : "the contact"}
            </span>
            . You'll be notified when the status changes — the messages
            themselves stay private between them and Journey.
          </div>
        )}

        {/* Footer actions */}
        <div className="pt-1 flex items-center gap-2 flex-wrap">
          {task.status !== "done" && (
            <Button
              variant="outline"
              onClick={async () => {
                await API.completeTask(task.id);
                await reload();
                onChange();
                toast.success("Task done");
              }}
              className="border-white/10 hover:bg-white/5"
              data-testid={`task-complete-${task.id}`}
            >
              <Check size={14} weight="bold" className="mr-1" />
              Mark done
            </Button>
          )}
          <Button
            variant="outline"
            onClick={async () => {
              await API.followupTask(task.id, 24);
              await reload();
              toast.success("Follow-up scheduled in 24h");
            }}
            className="border-white/10 hover:bg-white/5"
            data-testid={`task-followup-${task.id}`}
          >
            <Clock size={14} weight="bold" className="mr-1" />
            Follow-up 24h
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

function StatusDot({ status }) {
  let color = "bg-white/30";
  if (status === "sent" || status === "sent_stub" || status === "sent_dry_run") color = "bg-cyan-400";
  if (status === "pending_approval") color = "bg-amber-400";
  if (status === "rejected") color = "bg-red-400";
  if (status === "failed") color = "bg-red-500";
  if (status === "received") color = "bg-purple-400";
  return <span className={`inline-block w-1.5 h-1.5 rounded-full ${color}`} />;
}


/**
 * V9.27 — Inline chip showing how far the delegated-task deadline is.
 * • Future deadline → "Due in 4h" (cyan tone).
 * • Crossed deadline → "Overdue · 2h" (orange tone).
 * Pure presentational — receives the ISO string straight from the task.
 */
function DeadlineChip({ iso, testId }) {
  if (!iso) return null;
  let d;
  try {
    d = new Date(iso);
  } catch {
    return null;
  }
  const diff = d.getTime() - Date.now();
  const abs = Math.abs(diff);
  let label;
  if (abs < 60 * 1000) label = "now";
  else if (abs < 60 * 60 * 1000) label = `${Math.round(abs / 60000)}m`;
  else if (abs < 24 * 60 * 60 * 1000) label = `${Math.round(abs / 3600000)}h`;
  else label = `${Math.round(abs / 86400000)}d`;
  const overdue = diff < 0;
  const cls = overdue
    ? "border-orange-500/30 bg-orange-500/10 text-orange-300"
    : "border-cyan-500/25 bg-cyan-500/5 text-cyan-300/90";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider ${cls}`}
      title={d.toLocaleString()}
      data-testid={testId}
    >
      <Clock size={10} weight="bold" />
      {overdue ? `Overdue · ${label}` : `Due in ${label}`}
    </span>
  );
}


function AgentPanel({ task, onStart, onSimulate, onOverride, busy }) {
  const [simText, setSimText] = useState("");
  const isPending = task.status === "pending";
  const isTerminal = ["completed", "stuck", "ignored"].includes(task.status);
  const nudgeCount = task.nudge_count || 0;

  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      const diff = Date.now() - d.getTime();
      const abs = Math.abs(diff);
      const sign = diff > 0 ? "ago" : "from now";
      if (abs < 60000) return `just now`;
      if (abs < 3600000) return `${Math.round(abs / 60000)}m ${sign}`;
      if (abs < 86400000) return `${Math.round(abs / 3600000)}h ${sign}`;
      return `${Math.round(abs / 86400000)}d ${sign}`;
    } catch {
      return iso;
    }
  };

  return (
    <div className="rounded-2xl bg-gradient-to-br from-cyan-500/5 to-purple-500/5 border border-cyan-500/15 p-4 space-y-3" data-testid="agent-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-cyan-300/70 font-medium">
            Journey Agent
          </div>
          <div className="text-xs text-white/50 mt-1">
            {isPending && "Ready to start. Journey will introduce itself and follow up automatically."}
            {!isPending && !isTerminal &&
              `Active · ${nudgeCount}/3 nudges sent · next: ${fmt(task.next_nudge_at)}${task.last_intent ? ` · last intent: ${task.last_intent}` : ""}`}
            {isTerminal && `Terminal — ${task.status}. Override below to reactivate.`}
          </div>
        </div>
        {isPending && (
          <Button
            onClick={onStart}
            disabled={busy || !task.contact_id}
            size="sm"
            className="bg-cyan-500 hover:bg-cyan-400 text-black font-medium rounded-xl shrink-0"
            data-testid="agent-start-button"
          >
            <PaperPlaneTilt size={13} weight="bold" className="mr-1.5" />
            Start agent
          </Button>
        )}
      </div>

      {/* Simulate inbound — owner-side dev affordance that injects
          a synthetic reply into the contact↔Journey conversation.
          V9.29: HIDDEN for delegated tasks. The owner is supposed to
          stay out of that conversation; allowing them to simulate
          replies would be both confusing UX and a privacy footgun. */}
      {!isPending && !task.delegated && (
        <div className="flex gap-2">
          <Input
            placeholder="Simulate contact reply (e.g. 'Working on it!')"
            value={simText}
            onChange={(e) => setSimText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && simText.trim() && !busy) {
                onSimulate(simText);
                setSimText("");
              }
            }}
            className="bg-black/30 border-white/10 text-xs"
            data-testid="agent-simulate-input"
            disabled={busy}
          />
          <Button
            onClick={() => {
              onSimulate(simText);
              setSimText("");
            }}
            disabled={busy || !simText.trim()}
            size="sm"
            variant="outline"
            className="border-purple-500/30 text-purple-300 hover:bg-purple-500/10 shrink-0"
            data-testid="agent-simulate-button"
          >
            Simulate
          </Button>
        </div>
      )}

      {/* Manual status override */}
      <div className="flex flex-wrap gap-1.5">
        <span className="text-[10px] uppercase tracking-[0.16em] text-white/35 self-center pr-1">
          Override:
        </span>
        {["pending", "working_on_it", "stuck", "ignored", "completed"].map((s) => (
          <button
            key={s}
            onClick={() => onOverride(s)}
            disabled={busy || task.status === s}
            data-testid={`agent-override-${s}`}
            className={`px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
              task.status === s
                ? `${STATUS_COLOR[s]} border-current`
                : "bg-transparent border-white/10 text-white/50 hover:bg-white/5 hover:text-white/80"
            }`}
          >
            {STATUS_LABEL[s]}
          </button>
        ))}
      </div>
    </div>
  );
}
